import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldSelect } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import RiskModalFooter from "@/components/modals/profil-risk/risk-modal-footer";
import type { RiskUpdateModalProps } from "@/components/modals/profil-risk/risk-update-modal";
import { Separator } from "@/components/ui/separator";
import { patchStressSchema } from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { stateOptions, stressedOptions } from "@/utils/patient";

const GeneticModal: FC<RiskUpdateModalProps> = ({
  riskModalOpen,
  setRiskModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { stressState, stress, stressComment } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      stressState,
      stress,
      stressComment: stressComment ? stressComment : null,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchStressSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setRiskModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      description={
        <FieldSelect
          Field={Field}
          schema={patchStressSchema}
          name="stressState"
          options={stateOptions}
          validators={{
            onChange: patchStressSchema.shape.stressState,
          }}
          risk={"stress"}
        />
      }
      open={riskModalOpen !== "none"}
      onOpenChange={() => setRiskModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldSelect
          Field={Field}
          schema={patchStressSchema}
          name="stress"
          label="Niveau de stress"
          options={stressedOptions.map((option) => ({
            value: option.value.toString(),
            label: option.label,
          }))}
          type="number"
          validators={{
            onChange: patchStressSchema.shape.stress,
          }}
        />
        <FieldInput
          Field={Field}
          schema={patchStressSchema}
          name="stressComment"
          placeholder="Ajouter un commentaire"
          label="Commentaire"
          validators={{
            onChange: patchStressSchema.shape.stressComment,
          }}
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <RiskModalFooter
        setRiskModalOpen={setRiskModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default GeneticModal;
